<template>
  <v-container>
    <ValidationObserver
      ref="observer"
      v-slot="{ invalid }"
    >
      <v-row>
        <v-col cols="12">
          <v-divider />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <h4
            v-if="!isEdit"
            class="text-h7 text-uppercase mb-10"
          >
            Enter your curtain drop
          </h4>

          <v-divider />
        </v-col>
      </v-row>
      <v-form
        ref="form"
        style="width: 100%"
        class="py-5"
      >
        <form-instruction>
          <template slot="form">
            <h4 class="text-h4 text-uppercase mb-10">
              {{ isEdit ? 'Edit curtain dimensions' : 'Curtain dimensions' }}
            </h4>
            <ValidationProvider
              v-slot="{ errors }"
              ref="curtainWidth"
              :rules="curtainWidthRules"
              name="width"
            >
              <p class="d-flex justify-start align-center mb-3">
                Width
              </p>
              <v-text-field
                v-model="width"
                name="width"
                outlined
                dense
                solo
                flat
                type="text"
                label="Centimeter"
                :error-messages="errors"
              >
                <template slot="append">
                  Centimeter
                </template>
              </v-text-field>
            </ValidationProvider>
            <p class="mb-3">
              Drop {{ dropError }}
            </p>
            <v-text-field
              v-model="height"
              :loading="$wait.is('isLoadingCurtainPrice')"
              name="height"
              outlined
              dense
              solo
              flat
              type="text"
              label="Centimeter"
              :error-messages="dropError"
            >
              <template slot="append">
                Centimeter
              </template>
            </v-text-field>
            <v-img
              class="mt-5"
              src="@/assets/images/measuring-instructions.jpg"
            />
          </template>
          <template slot="text">
            <h4 class="text-h4 text-uppercase mb-10">
              Measuring instructions
            </h4>
            <ul>
              <li>
                Please use a metal tape measure or ruler that is long enough to
                measure the length and width of the window.
              </li>
              <li>Please measure at least twice to avoid mistakes.</li>
              <li>Please be aware of any obstacles around the window.</li>
            </ul>
            <template v-if="withRailRod && curtainType === 'ringcurtain'">
              <ul>
                <li>
                  With an equal pair we have added an extra 5 cm to create extra fullness at the closure to avoid light coming through.
                </li>
              </ul>
            </template>
            <strong>How do I measure the drop</strong>
            <ul>
              <li>
                Please measure from the fixing point where the track/rod will be
                installed or top of the window to either the floor, windowsill,
                or the place you would like the curtain to finish.
              </li>
              <li>
                Measure on both sides of the window and please note the shortest
                drop. The ceiling, the floor, or the window frame are not always
                square or straight.
              </li>
              <li>
                If you don’t have flooring installed, please deduct 2 cm for
                carpets and 1.5 cm for hard flooring.
              </li>
              <li>
                Please enter this dimension as the desired drop of your curtain.
              </li>
            </ul>
            <ValidationProvider
              v-slot="{ errors }"
              :rules="instructionsRules"
              name="instruction"
            >
              <v-checkbox
                v-model="readApprove"
                :error-messages="errors"
                value="1"
                class="mb-8"
                label="I have read the measuring instructions."
                name="instruction"
              />
            </ValidationProvider>
            <p
              v-if="readApprove && isEdit && accessory"
              style="color: red; margin-bottom: 0;"
            >
              You have just edited your curtain size. Please don't forget to edit your {{ accessory }} if required.
            </p>
          </template>
        </form-instruction>
      </v-form>
      <v-row v-if="!isEdit">
        <v-col cols="12">
          <v-divider />
        </v-col>
        <v-col
          cols="12"
          md="6"
        >
          <fabric-picker-card />
        </v-col>
        <v-col
          cols="12"
          md="6"
        >
          <div class="confirmButtonsBlock">
            <v-btn
              class="primary opacity-bg"
              @click="back"
            >
              <v-icon class="white--text pr-5">
                mdi mdi-chevron-left
              </v-icon>
              Previous step
            </v-btn>
            <v-btn
              class="primary"
              :disabled="invalid ||$wait.is('isLoadingCurtainPrice') ||isDropError"
              @click="next"
            >
              Next step
              <v-icon class="white--text pl-5">
                mdi mdi-chevron-right
              </v-icon>
            </v-btn>
          </div>
        </v-col>
      </v-row>
      <v-row v-else>
        <v-col cols="12">
          <v-divider />
        </v-col>
        <v-col
          cols="12"
          md="6"
        >
          <fabric-picker-card />
        </v-col>
        <v-col
          cols="12"
          md="6"
        >
          <div class="confirmButtonsBlock">
            <v-btn
              v-if="withRailRod"
              class="primary opacity-bg"
              @click="nextStep"
            >
              Edit {{ accessory }}
            </v-btn>
            <save-edit-button
              :disabled="invalid || !room"
              :loading="$wait.is('isArticleUpdating') || isDropError"
              :save-button-state="saveButtonState"
              @next="next"
            />
          </div>
        </v-col>
      </v-row>
    </ValidationObserver>
  </v-container>
</template>

<script>
import FormInstruction from '@/components/FlowComponents/FormInstruction'
import SaveEditButton from '@/components/Buttons/SaveEditButton'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { mapActions, mapGetters } from 'vuex'
import { TRACK_RAIL, SPOTLIGHT_ROD } from '@/constants.js'
import { EditPropertyMixin } from '@/mixins/EditFlow'

const FabricPickerCard = () => import('@/components/Cards/FabricPickerCard')

export default {
  name: 'CurtainDropPicker',
  components: {
    FormInstruction,
    FabricPickerCard,
    ValidationObserver,
    ValidationProvider,
    SaveEditButton
  },
  mixins: [EditPropertyMixin],
  props: {
    saveButtonState: {
      type: String,
      default: "default"
    },
  },
  data() {
    return {
      readApprove: null,
      showInformDialog: false,
      informDetails: {},
    }
  },
  computed: {
    ...mapGetters({
      template: 'order/getCurtainTemplate',
      room: 'order/getRoom',
      railRod: 'order/getRailRod',
      resetKey: 'order/getResetKey',
      railRodType: 'order/getRailRodType',
      curtainType: 'order/getCurtainType',
      withRailRod: 'order/getWithRailRod',
      validations: 'order/getCurtainValidations',
      curtainParams: 'order/getCurtainParameters',
      railRodParams: 'order/getRailRodParameters',
      dropError:'order/getDropError'
    }),
    isDropError() {
      return !!this.dropError
    },
    accessory() {
      if (this.withRailRod) {
        return this.railRodType === TRACK_RAIL ? 'track' : 'rod'
      } else {
        return ''
      }
    },
    curtainWidthRules() {
      const validation = this.template['width_definition']
      const accessoryLength = this.railRodParams['width']
      if (!validation) return 'required|double:1'
      const max =
        this.withRailRod && this.railRodType === SPOTLIGHT_ROD
          ? Number(accessoryLength) + 5
          : this.railRodType === SPOTLIGHT_ROD
          ? 360
          : validation.max
      const min = validation.min
      return `required|double:1|min_value:${min}|max_value:${max}`
    },
    curtainHeightRules() {
      const validation = this.template['height_definition']
      validation.max = 283
      return validation ? validation.schemaExtension.validation : 'required'
    },
    instructionsRules() {
      return this.validations['instructions'] || 'required'
    },
    width: {
      set(val) {
        this.addCurtainParameter({ width: val })
      },
      get() {
        return Number(this.curtainParams['width']) || null
      }
    },
    height: {
      set(val) {
        this.addCurtainParameter({ height: val })
      },
      get() {
        return this.curtainParams['height'] || null
      }
    }
  },
  watch: {
    curtainWidthRules() {
      if (this.width) {
        this.$refs.curtainWidth.validate()
      }
    },
    resetKey() {
      this.$refs.observer.reset()
      this.readApprove = null
    }
  },
  methods: {
    ...mapActions({
      addCurtainParameter: 'order/addCurtainParameter'
    }),
    back() {
      this.$emit('back')
    },
    next() {
      this.$emit('next')
    },
    nextStep() {
      this.$emit('nextStep')
    },
    openInformDialog() {
      this.informDetails = {
        description: this.$i18n.t('components.inform.widthDescription')
      }
      this.showInformDialog = true
    },
    closeInformDialog() {
      this.showInformDialog = false
      this.curtainDetails = {}
    }
  }
}
</script>

<style scoped lang="scss">
strong,
ul li {
  line-height: 1.9;
}

.v-text-field .v-input__append-inner {
  color: gray;
}
</style>
